import { Button } from '../../shared/ui'
import type { CustomFieldDef } from '../../lib/outreach/types'
import type { ContactsFilters } from './useContacts'

/**
 * Collapsing panel of the 8 secondary filters: country, location,
 * phone-prefix, score min/max, tag, last-contacted, phone-status,
 * email-status. Same semantics as legacy clearAdvFilters /
 * loadContacts read paths.
 */

interface Props {
  filters: ContactsFilters
  onChange: (patch: Partial<ContactsFilters>) => void
  onClear: () => void
  open: boolean
  /** Contact custom fields of the active business, for the custom field filter. */
  customDefs?: CustomFieldDef[]
}

const PHONE_STATUSES = [
  'Good',
  'Bad Number',
  'Wrong Number',
  'Disconnected',
  'Needs Number',
] as const
const EMAIL_STATUSES = ['Good', 'Bounced', 'Invalid', 'Unsubscribed', 'Needs Email'] as const
const LAST_CONTACTED = [
  { value: '', label: 'Last contacted: any' },
  { value: 'never', label: 'Never contacted' },
  { value: '7', label: 'Within 7 days' },
  { value: '30', label: 'Within 30 days' },
  { value: '90', label: 'Within 90 days' },
] as const

export function ContactsAdvancedFilters({
  filters,
  onChange,
  onClear,
  open,
  customDefs = [],
}: Props) {
  if (!open) return null
  const activeDef = customDefs.find((d) => d.key === filters.customKey)

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        padding: '12px 0',
        borderTop: '1px solid var(--border)',
        marginBottom: 16,
        alignItems: 'center',
      }}
    >
      <input
        className="filter-select"
        id="c-country"
        placeholder="Country…"
        value={filters.country}
        onChange={(e) => onChange({ country: e.target.value })}
        style={{ width: 140 }}
      />
      <input
        className="filter-select"
        id="c-location"
        placeholder="City / state / country…"
        value={filters.location}
        onChange={(e) => onChange({ location: e.target.value })}
        style={{ width: 200 }}
      />
      <input
        className="filter-select"
        id="c-phone-prefix"
        placeholder="Phone +"
        value={filters.phonePrefix}
        onChange={(e) => onChange({ phonePrefix: e.target.value })}
        style={{ width: 100 }}
      />
      <input
        className="filter-select"
        id="c-score-min"
        type="number"
        min={0}
        max={100}
        placeholder="Score ≥"
        value={filters.scoreMin}
        onChange={(e) => onChange({ scoreMin: e.target.value })}
        style={{ width: 90 }}
      />
      <input
        className="filter-select"
        id="c-score-max"
        type="number"
        min={0}
        max={100}
        placeholder="Score ≤"
        value={filters.scoreMax}
        onChange={(e) => onChange({ scoreMax: e.target.value })}
        style={{ width: 90 }}
      />
      <input
        className="filter-select"
        id="c-tag-filter"
        placeholder="Tag"
        value={filters.tagFilter}
        onChange={(e) => onChange({ tagFilter: e.target.value })}
        style={{ width: 120 }}
      />
      <select
        className="filter-select"
        id="c-last-contacted"
        value={filters.lastContacted}
        onChange={(e) => onChange({ lastContacted: e.target.value })}
      >
        {LAST_CONTACTED.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <select
        className="filter-select"
        id="c-phone-status"
        value={filters.phoneStatus}
        onChange={(e) => onChange({ phoneStatus: e.target.value })}
      >
        <option value="">Phone status: any</option>
        {PHONE_STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <select
        className="filter-select"
        id="c-email-status"
        value={filters.emailStatus}
        onChange={(e) => onChange({ emailStatus: e.target.value })}
      >
        <option value="">Email status: any</option>
        {EMAIL_STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      {customDefs.length > 0 && (
        <>
          <select
            className="filter-select"
            id="c-custom-key"
            aria-label="Custom field"
            value={filters.customKey}
            onChange={(e) => {
              const def = customDefs.find((d) => d.key === e.target.value)
              onChange({ customKey: e.target.value, customType: def?.type ?? '', customValue: '' })
            }}
          >
            <option value="">Custom field: any</option>
            {customDefs.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
          {activeDef &&
            (activeDef.options.length > 0 ? (
              <select
                className="filter-select"
                id="c-custom-value"
                aria-label={`${activeDef.label} value`}
                value={filters.customValue}
                onChange={(e) => onChange({ customValue: e.target.value })}
              >
                <option value="">Choose…</option>
                {activeDef.options.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            ) : (
              <input
                className="filter-select"
                id="c-custom-value"
                aria-label={`${activeDef.label} value`}
                placeholder="Equals…"
                value={filters.customValue}
                onChange={(e) => onChange({ customValue: e.target.value })}
                style={{ width: 140 }}
              />
            ))}
        </>
      )}
      <Button variant="secondary" onClick={onClear} style={{ marginLeft: 'auto' }}>
        Clear
      </Button>
    </div>
  )
}
